import { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { User } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  profile: any | null;
  loading: boolean;
  error: Error | null;
  initialized: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ data: any; error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    loading: true,
    error: null,
    initialized: false
  });

  // Mock auth with localStorage persistence
  useEffect(() => {
    const storedUser = localStorage.getItem('mockUser');
    const storedProfile = localStorage.getItem('mockProfile');
    
    if (storedUser && storedProfile) {
      setState(prev => ({
        ...prev,
        user: JSON.parse(storedUser),
        profile: JSON.parse(storedProfile),
        loading: false,
        initialized: true
      }));
    } else {
      setState(prev => ({
        ...prev,
        loading: false,
        initialized: true
      }));
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      // Mock successful login
      const mockUser = {
        id: '123',
        email,
        user_metadata: { full_name: 'Test User' }
      };

      const mockProfile = {
        id: '123',
        user_id: '123',
        email,
        onboarding_progress: {
          isComplete: false,
          currentStage: 'WELCOME',
          completedStages: []
        },
        career_coins: 0,
        profile_completion: 0
      };

      // Persist mock auth state
      localStorage.setItem('mockUser', JSON.stringify(mockUser));
      localStorage.setItem('mockProfile', JSON.stringify(mockProfile));

      setState(prev => ({
        ...prev,
        user: mockUser as any,
        profile: mockProfile,
        error: null
      }));

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      // Mock successful signup
      const mockUser = {
        id: '123',
        email,
        user_metadata: { full_name: 'New User' }
      };

      const mockProfile = {
        id: '123',
        user_id: '123',
        email,
        onboarding_progress: {
          isComplete: false,
          currentStage: 'WELCOME',
          completedStages: []
        },
        career_coins: 0,
        profile_completion: 0
      };

      // Persist mock auth state
      localStorage.setItem('mockUser', JSON.stringify(mockUser));
      localStorage.setItem('mockProfile', JSON.stringify(mockProfile));

      setState(prev => ({
        ...prev,
        user: mockUser as any,
        profile: mockProfile,
        error: null
      }));

      return { data: mockUser, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  };

  const signOut = async () => {
    // Clear mock auth state
    localStorage.removeItem('mockUser');
    localStorage.removeItem('mockProfile');
    
    setState({
      user: null,
      profile: null,
      loading: false,
      error: null,
      initialized: true
    });
  };

  const refreshProfile = async () => {
    if (state.user) {
      const mockProfile = {
        id: '123',
        user_id: state.user.id,
        email: state.user.email,
        onboarding_progress: {
          isComplete: false,
          currentStage: 'WELCOME',
          completedStages: []
        },
        career_coins: 0,
        profile_completion: 0
      };

      localStorage.setItem('mockProfile', JSON.stringify(mockProfile));
      
      setState(prev => ({
        ...prev,
        profile: mockProfile
      }));
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        ...state, 
        signIn, 
        signUp, 
        signOut,
        refreshProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}